import { useState } from 'react';
import styled from 'styled-components';

import { breakpoints } from '@/shared/styles';
import { Theme, useTheme } from '@/services/theme';
import { curatorsList } from '@/pages/LandingPage/constants/curators';

import { ReactComponent as Balancer } from './defi/balancer.svg';
import { ReactComponent as Ethena } from './defi/ethena.svg';
import { ReactComponent as Fuel } from './defi/fuel.svg';
import { ReactComponent as Gearbox } from './defi/gearbox.svg';
import { ReactComponent as Lido } from './defi/lido.svg';
import { ReactComponent as Marginly } from './defi/marginly.svg';
import { ReactComponent as Pendle } from './defi/pendle.svg';
import { ReactComponent as Silo } from './defi/silo.svg';
import { ReactComponent as Spectra } from './defi/spectra.svg';
import { ReactComponent as Stakestone } from './defi/stakestone.svg';
import { ReactComponent as Swell } from './defi/swell.svg';
import { ReactComponent as Zerolend } from './defi/zerolend.svg';
import { ReactComponent as Zircuit } from './defi/zircuit.svg';
import { ReactComponent as P2p } from './node-operators/p2p.svg';
import { ReactComponent as Infrasingularity } from './node-operators/infrasingularity.svg';
import { ReactComponent as Rockx } from './node-operators/rockx.svg';
import { ReactComponent as Chorusone } from './node-operators/chorusone.svg';
import { ReactComponent as Luganodes } from './node-operators/luganodes.svg';
import { ReactComponent as Piertwo } from './node-operators/piertwo.svg';
import { ReactComponent as Bwarelabs } from './node-operators/bwarelabs.svg';
import { ReactComponent as Nodeinfra } from './node-operators/nodeinfra.svg';
import { ReactComponent as Kiln } from './node-operators/kiln.svg';
import { ReactComponent as Cp0x } from './node-operators/cp0x.svg';
import { ReactComponent as Blockspace } from './node-operators/blockspace.svg';
import { ReactComponent as Staketab } from './node-operators/staketab.svg';
import { ReactComponent as Certik } from './node-operators/certik.svg';
import { ReactComponent as Stakecapital } from './node-operators/stakecapital.svg';
import { ReactComponent as Popsteam } from './node-operators/popsteam.svg';

const defiPartners = [
  Balancer,
  Ethena,
  Fuel,
  Gearbox,
  Lido,
  Marginly,
  Pendle,
  Silo,
  Zerolend,
  Swell,
  Stakestone,
  Spectra,
  Zircuit,
];

const nodeOperatorsPartners = [
  P2p,
  Infrasingularity,
  Rockx,
  Chorusone,
  Luganodes,
  Piertwo,
  Bwarelabs,
  Nodeinfra,
  Kiln,
  Cp0x,
  Blockspace,
  Staketab,
  Certik,
  Stakecapital,
  Popsteam,
];

type StyledProps = {
  $isActive?: boolean;
  theme: Theme;
  $disabled?: boolean;
};

const breakpoint = 'min-width: 800px';

const StyledTabs = styled.div`
  align-items: center;
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  gap: 30px;
  position: relative;
  margin-bottom: 20px;

  padding: 10px 0px 0;
  @media (${breakpoint}) {
    padding: 10px 0px 0;
  }

  border-bottom: 1px solid ${({ theme }: StyledProps) => theme.colors.newGray1};
`;

const StyledTab = styled.button`
  background-color: transparent;
  border: none;
  color: ${({ theme, $disabled }: StyledProps) =>
    $disabled ? theme.colors.text.tertiary : theme.colors.text.primary};
  cursor: pointer;
  display: inline-block;
  position: relative;
  text-align: center;
  font-family: Gramatika;
  font-weight: 700;

  pointer-events: ${({ $disabled }: StyledProps) => ($disabled ? 'none' : 'unset')};

  padding: 20px 0;
  font-size: 20px;

  @media (${breakpoint}) {
    padding: 20px 0;
    font-size: 32px;
    line-height: 130%;
  }

  &::after {
    background: rgba(237, 25, 67, 1);
    content: '';
    opacity: ${({ $isActive }: StyledProps) => ($isActive ? 1 : 0)};
    position: absolute;
    bottom: -1px;
    left: 0;
    right: 0;
    height: 6px;
    transition: opacity 250ms;
  }
`;

const Container = styled.section`
  position: relative;
  margin-top: 60px;
  padding: 0 20px;

  @media (min-width: ${breakpoints.tabletSM}px) {
    margin-top: 180px;
    padding: 0 16px;
  }

  @media (min-width: ${breakpoints.laptopSM}px) {
    padding: 0;
  }
`;

const StyledWrapper = styled.div`
  padding: 0 0 60px 0;

  @media (min-width: ${breakpoints.tabletMD}px) {
    padding: 0 0 161px 0;
  }
`;

const Grid = styled.div`
  margin-top: 56px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: auto;
  justify-items: center;
  font-family: Gramatika;

  border-radius: 16px;
  gap: 12px;

  @media (min-width: ${breakpoints.tabletSM}px) {
    gap: 16px;
    grid-template-columns: repeat(3, 1fr);
  }

  @media (min-width: ${breakpoints.tabletLG}px) {
    grid-template-columns: repeat(4, 1fr);
  }

  @media (min-width: ${breakpoints.laptop}px) {
    grid-template-columns: repeat(5, 1fr);
  }

  .Curators__card {
    width: 100%;
    height: 66px;

    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;

    border: 1px solid rgba(41, 42, 53, 1);
    border-radius: 18px;
    padding: 10px 6px;

    & svg,
    & img {
      height: 100%;
      max-height: 20px !important;
      width: auto;
      object-fit: contain;
      object-position: center;
    }

    @media (min-width: ${breakpoints.tabletSM}px) {
      height: 88px;
      border-radius: 20px;
      padding: 20px 24px;

      & svg,
      & img {
        max-height: 24px !important;
      }
    }

    @media (min-width: ${breakpoints.tabletMD}px) {
      gap: 16px;
      border-radius: 24px;
      height: 96px;
    }
  }

  .DeFi__card {
    width: 100%;
    height: 66px;

    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;

    border: 1px solid rgba(41, 42, 53, 1);
    border-radius: 18px;
    padding: 10px 6px;

    & svg,
    & img {
      height: 30px;
      width: auto;
      object-fit: contain;
      object-position: center;
    }

    @media (min-width: ${breakpoints.tabletSM}px) {
      height: 88px;
      border-radius: 20px;
      padding: 20px 24px;

      & svg,
      & img {
        height: 40px;
      }
    }

    @media (min-width: ${breakpoints.tabletMD}px) {
      gap: 16px;
      border-radius: 24px;
      height: 96px;
    }
  }
`;

type TabName = 'curators' | 'defi' | 'node-operators';

export const Partners = () => {
  const { theme } = useTheme();
  const [tab, setTab] = useState<TabName>('curators');

  return (
    <StyledWrapper>
      <Container>
        <h2>Partners</h2>

        <StyledTabs theme={theme}>
          <StyledTab
            $isActive={tab === 'curators'}
            type="button"
            onClick={() => setTab('curators')}
            theme={theme}
          >
            Curators
          </StyledTab>
          <StyledTab
            $isActive={tab === 'defi'}
            type="button"
            onClick={() => setTab('defi')}
            theme={theme}
          >
            DeFi
          </StyledTab>
          <StyledTab
            $isActive={tab === 'node-operators'}
            type="button"
            onClick={() => setTab('node-operators')}
            theme={theme}
          >
            Node Operators
          </StyledTab>
        </StyledTabs>

        {tab === 'curators' && (
          <Grid theme={theme}>
            {curatorsList.map((Image, index) => (
              <div key={`curator_${index}`} className="Curators__card">
                {Image}
              </div>
            ))}
          </Grid>
        )}

        {tab === 'defi' && (
          <Grid theme={theme}>
            {defiPartners.map((Image, index) => (
              <div key={`defi_${index}`} className="DeFi__card">
                <Image />
              </div>
            ))}
          </Grid>
        )}

        {tab === 'node-operators' && (
          <Grid theme={theme}>
            {nodeOperatorsPartners.map((Image, index) => (
              <div key={`node_operator_${index}`} className="DeFi__card">
                <Image />
              </div>
            ))}
          </Grid>
        )}
      </Container>
    </StyledWrapper>
  );
};
